import React, { useEffect, useState, useRef } from 'react';
import '../styles/VerifyProperty.css';
import { Dropdown, Input, Loading, PropertyIcon, Switch, ReportLoadingAnimation, Modal, FinishLevel, ImageGallery, ScrollableImages, SelectionChip, DataPullAnimation, Radio } from '../components';
import { ADU, Arrow, Bathroom, Bedroom, RealEstateAPI, Realtor, Redfin, SquareFootage, YearBuilt, Zillow } from '../assets/icons';
import { colour, numberFormatter } from '../styles/GlobalStyles';
import { Button } from '../components';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useUI } from '../contexts';
import { checkOnAuthStateChanged, cloudFunctionV2, mergeSubData, sendErrorEmail, setDataRandomId, recordEvent, getSubDocument, getDocument } from '../functions';
import { NoMatchModal } from '../modalbody';
import { conditionLevels, propertyDataReAPI, kansasCityCountyFIPS } from '../defaults';
import { HugeiconsIcon } from '@hugeicons/react';
import { CarParking01Icon, PoolIcon, EntranceStairsIcon } from '@hugeicons/core-free-icons';

import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { PROPERTY_TYPE_OPTIONS, normalizePropertyTypeKey } from '../utils/propertyTypeUtils';

function VerifyProperty({ rerunMode = false, initialProperty = null, initialCompanyId = null, initialPropertyEstimates = null, initialPropertyNotes = null, initialRemodelLevel = null, initialImageUrl = null, onRerunComplete = null, onRerunBack = null, leadMode = false, onLeadComplete = null, onLeadBack = null, submitting = false }) {
    // Embedded modes (rerunMode | leadMode) treat the property as already
    // hydrated from the caller, skip the property_searches listener, and hand
    // results back through a callback instead of navigating. Use this flag
    // anywhere we'd previously branched on `rerunMode === true`.
    const embeddedMode = rerunMode === true || leadMode === true;
    const origin = window.location.origin;
    const testMode = origin.includes("localhost");
    const [userId, setUserId] = useState(null);
    const { setShowDarkBackground } = useUI();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [adu, setAdu] = useState(initialProperty?.propertyInfo?.adu ?? false);
    const [pool, setPool] = useState(initialProperty?.propertyInfo?.pool ?? false);
    const [garage, setGarage] = useState(initialProperty?.propertyInfo?.garage ?? false);
    const [garageSpaces, setGarageSpaces] = useState(initialProperty?.propertyInfo?.garageSpaces ?? 1);
    const [garageType, setGarageType] = useState(initialProperty?.propertyInfo?.garageType ?? null);
    const [basement, setBasement] = useState(initialProperty?.propertyInfo?.basement ?? false);
    const [insights, setInsights] = useState(false);
    const [openScrollableImages, setOpenScrollableImages] = useState(false);
    const [openImageGallery, setOpenImageGallery] = useState(false);
    const [selectedConditionLevel, setSelectedConditionLevel] = useState(
        typeof initialRemodelLevel === 'number' ? initialRemodelLevel : null,
    );
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [amenityDescription, setAmenityDescription] = useState(null);
    const [dataOptions, setDataOptions] = useState([]);
    const [images, setImages] = useState([]);
    const [openPropertyImageGallery, setOpenPropertyImageGallery] = useState(false);
    const [selectedPropertyImageIndex, setSelectedPropertyImageIndex] = useState(0);
    const [zillowURL, setZillowURL] = useState(null);
    const [realtorURL, setRealtorURL] = useState(null);
    const [redfinURL, setRedfinURL] = useState(null);
    const [propertyEstimates, setPropertyEstimates] = useState(initialPropertyEstimates);
    const [forSale, setForSale] = useState(null);
    // Default to "share_link" in leadMode — wholesaler pastes don't carry
    // MLS data and the parser typically extracts a Drive/Dropbox folder.
    const [imageSourceType, setImageSourceType] = useState(leadMode ? "share_link" : null); // "mls" or "share_link"
    // leadMode now waits behind the DataPullAnimation until checkSubjectAmenities
    // lands the multi-source pull (mirrors the standalone /verify-property route),
    // so it starts in the loading state. rerunMode has its own already-hydrated
    // property and no property_searches listener, so it stays false.
    const [dataLoading, setDataLoading] = useState(rerunMode === true ? false : true);
    const location = useLocation();
    const state = location.state;
    const params = useParams();
    const routeCountyId = params?.countyId || null;
    const routePropertyId = params?.id || params?.propertyId || null;
    const navigate = useNavigate();
    const [propertyState, setPropertyState] = useState(initialProperty || (state === null ? null : state.data));
    const [companyIdState, setCompanyIdState] = useState(initialCompanyId || (state === null ? null : state.companyId || null));
    const property = propertyState;
    const unsubscribeRef = useRef(null);
    // Always show the image-URL input in leadMode — wholesaler pastes
    // typically include a Drive / Dropbox folder, and the parser already
    // extracted it; surfacing the input lets the user confirm / correct it
    // before we hand the URL off to the repair-cost analyzer downstream.
    // testMode keeps it visible on localhost regardless.
    const showImageInput = testMode || leadMode;

    // Normalize potentially missing/invalid URL-ish values to either a clean absolute URL or null
    const normalizeUrlOrNull = (val) => {
        if (typeof val !== "string") return null;
        const s = val.trim();
        if (!s) return null;
        try {
            const u = new URL(s);
            // Only allow http(s)
            if (u.protocol !== "http:" && u.protocol !== "https:") return null;
            return u.href;
        } catch {
            return null;
        }
    };
    useEffect(() => {
        if (!embeddedMode) {
            document.title = "Verify Property | Clozers";
        }

        const fetchUserData = async () => {
            const user = await checkOnAuthStateChanged();
            if (user.status === 200) {
                setUserId(user.userId);
                // rerunMode reads companies/.../properties/{id}, not
                // property_searches/{id}, so it has nothing to subscribe to.
                // leadMode does — it expects checkSubjectAmenities to land
                // multi-source columns + imageURLs into the searches doc.
                if (rerunMode !== true) {
                    startListener();
                }
            }
        };

        const startListener = () => {
            if (!property || !property.id) return;

            const db = getFirestore();
            const ref = doc(db, 'property_searches', String(property.id));

            // Avoid duplicate listeners
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }

            unsubscribeRef.current = onSnapshot(ref, (snap) => {
                // If the document doesn't exist yet, keep listening
                if (!snap.exists()) return;

                // Don't unsubscribe until the doc has actual amenity data —
                // getReAPIPropertyDetails writes a stub with only distressSignals
                // before checkSubjectAmenities finishes writing the full result.
                const raw = snap.data()?.data || {};
                if (raw.description === undefined) return;

                // Full amenity data present → stop listening
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                    unsubscribeRef.current = null;
                }

                try {
                // leadMode now holds the form behind the DataPullAnimation until
                // this data lands, so there's no in-progress user edit to clobber
                // — prefill garage/pool/basement just like the standalone route.
                // Listing-sourced values (Zillow/Realtor) flow through these same
                // raw fields, falling back to LLM vision when the listings are
                // silent (precedence handled server-side in checkSubjectAmenities).
                if ( raw.garage ) {
                    setGarage(raw.garage)
                }
                if ( raw.garageSpaces !== undefined && raw.garageSpaces !== null ) {
                    const spaces = Number(raw.garageSpaces);
                    if ( !isNaN(spaces) && spaces >= 0 ) {
                        setGarageSpaces(spaces);
                    }
                }
                if ( raw.garageType ) {
                    setGarageType(raw.garageType)
                }
                if ( raw.pool ) {
                    setPool(raw.pool)
                }
                if ( raw.basement !== undefined ) {
                    setBasement(raw.basement)
                }
                if ( raw.basementSqFootage !== undefined && raw.basementSqFootage !== null ) {
                    const sqft = Number(raw.basementSqFootage);
                    if ( !isNaN(sqft) && sqft >= 0 ) {
                        setBelowGradeSquareFootage(sqft);
                    }
                }
                if ( raw.description ) {
                    setAmenityDescription(raw.description)
                }
                if ( raw.imageURLs ) {
                    setImages(raw.imageURLs)
                }

                // Extract forSale boolean
                if ( raw.forSale !== undefined && raw.forSale !== null ) {
                    setForSale(raw.forSale);
                }

                setRealtorURL(normalizeUrlOrNull(raw?.propertyInfo?.realtor?.url));
                setZillowURL(normalizeUrlOrNull(raw?.propertyInfo?.zillow?.url));
                setRedfinURL(normalizeUrlOrNull(raw?.propertyInfo?.redfin?.url));

                // Extract property estimates and URLs into unified structure
                const propertyInfo = raw?.propertyInfo || {};
                const estimates = {
                    zillow: {
                        estimate: propertyInfo.zillow?.estimate ?? null,
                        url: normalizeUrlOrNull(propertyInfo.zillow?.url)
                    },
                    redfin: {
                        estimate: propertyInfo.redfin?.estimate ?? null,
                        url: normalizeUrlOrNull(propertyInfo.redfin?.url)
                    },
                    realtor: {
                        estimate: propertyInfo.realtor?.estimate ?? null,
                        url: normalizeUrlOrNull(propertyInfo.realtor?.url)
                    }
                };
                setPropertyEstimates(estimates);

                // Build merged chip options with "mode" selection (most common value)
                const providerPriority = ["zillow","redfin","realtor","realestateapi"]; // tie-breaker order

                const normalize = (v) => {
                    if (v === null || v === undefined || v === "") return null;
                    const n = Number(v);
                    return Number.isFinite(n) ? n : null;
                };

                const makeMerged = (entries) => {
                    // entries: [{provider, value}]
                    const byValue = new Map(); // value(string) -> Set(providers)
                    entries.forEach(({ provider, value }) => {
                        const norm = normalize(value);
                        if (norm === null || norm === 0) return; // ignore null/0 per requirements
                        const key = String(norm);
                        if (!byValue.has(key)) byValue.set(key, new Set());
                        byValue.get(key).add(provider);
                    });

                    const merged = Array.from(byValue.entries()).map(([key, provSet]) => {
                        const provArr = Array.from(provSet);
                        const rawVal = Number(key);
                        return {
                            providers: provArr,              // NEW: all providers that agree on this value
                            provider: provArr[0],            // BACK-COMPAT: first provider
                            rawValue: rawVal,
                            value: numberFormatter.format(rawVal),
                            selected: false
                        };
                    });

                    // Default selection = mode (most providers). Tie-breaker by providerPriority.
                    if (merged.length > 0) {
                        let maxCount = Math.max(...merged.map(e => e.providers.length));
                        let candidates = merged.filter(e => e.providers.length === maxCount);
                        const score = (entry) => {
                            for (let p of providerPriority) {
                                if (entry.providers.includes(p)) return providerPriority.length - providerPriority.indexOf(p);
                            }
                            return 0;
                        };
                        candidates.sort((a, b) => score(b) - score(a));
                        const selectedVal = candidates[0].rawValue;
                        merged.forEach(e => { e.selected = (e.rawValue === selectedVal); });
                    }
                    // If there are no valid values (all null/0), nothing is selected per requirement.
                    return merged;
                };

                const bedroomsMerged = makeMerged([
                    { provider: "redfin", value: raw?.propertyInfo?.redfin?.bedrooms },
                    { provider: "zillow", value: raw?.propertyInfo?.zillow?.bedrooms },
                    { provider: "realtor", value: raw?.propertyInfo?.realtor?.bedrooms },
                    { provider: "realestateapi", value: property?.propertyInfo?.bedrooms }
                ]);

                const bathroomsMerged = makeMerged([
                    { provider: "redfin", value: raw?.propertyInfo?.redfin?.bathrooms },
                    { provider: "zillow", value: raw?.propertyInfo?.zillow?.bathrooms },
                    { provider: "realtor", value: raw?.propertyInfo?.realtor?.bathrooms },
                    { provider: "realestateapi", value: property?.propertyInfo?.bathrooms }
                ]);

                const sqftMerged = makeMerged([
                    { provider: "redfin", value: raw?.propertyInfo?.redfin?.sqft },
                    { provider: "zillow", value: raw?.propertyInfo?.zillow?.sqft },
                    { provider: "realtor", value: raw?.propertyInfo?.realtor?.sqft },
                    { provider: "realestateapi", value: property?.propertyInfo?.livingSquareFeet }
                ]);

                // Helper to safely get the chosen raw value from a merged array.
                const getSelectedRaw = (mergedArr) => {
                    if (!Array.isArray(mergedArr) || mergedArr.length === 0) return null;
                    const chosen = mergedArr.find(e => e && e.selected === true);
                    const candidate = chosen?.rawValue ?? mergedArr[0]?.rawValue ?? null;
                    return (candidate === null || Number.isNaN(Number(candidate))) ? null : candidate;
                };

                const selectedBeds = getSelectedRaw(bedroomsMerged);
                const selectedBaths = getSelectedRaw(bathroomsMerged);
                const selectedSqft = getSelectedRaw(sqftMerged);

                // Only update state when we have a valid number; otherwise
                // keep prior user-entered value. leadMode now lands these before
                // the form renders (held behind the loader), so the mode chip is
                // both pre-selected and reflected in the input — same as the
                // standalone route. Users can still click another chip to switch.
                if (selectedBeds !== null) setBedrooms(selectedBeds);
                if (selectedBaths !== null) setBathrooms(selectedBaths);
                if (selectedSqft !== null) setSquareFootage(selectedSqft);

                const newOptions = [
                    { type: "bedrooms", values: Array.isArray(bedroomsMerged) ? bedroomsMerged : [] },
                    { type: "bathrooms", values: Array.isArray(bathroomsMerged) ? bathroomsMerged : [] },
                    { type: "sqFootage", values: Array.isArray(sqftMerged) ? sqftMerged : [] }
                ];
                setDataOptions(newOptions);
                setDataLoading(false);
                } catch (err) {
                    console.error("Error processing Firestore snapshot:", err);
                    setDataLoading(false);
                }
            }, (error) => {
                console.error("Firestore listener error:", error);
                setDataLoading(false);
            });
        };

        fetchUserData();

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [property, embeddedMode, rerunMode, leadMode]);

    // Safety net: leadMode holds the form behind DataPullAnimation until the
    // property_searches doc lands. If checkSubjectAmenities is pathologically
    // slow, errors, or never writes, reveal the editable (REAPI-based) form after
    // 25s so the user is never stranded on the animation. We also TEAR DOWN the
    // listener here — once the user is in the form, a late-arriving snapshot must
    // not overwrite edits they've started making (the prefill no longer guards on
    // leadMode). Lost multi-source chips in this rare case are acceptable:
    // createLeadManual re-enriches server-side on submit regardless. Clears when
    // data lands normally (dataLoading flips false) or on unmount.
    useEffect(() => {
        if (leadMode !== true || dataLoading !== true) return;
        const timer = setTimeout(() => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            setDataLoading(false);
        }, 25000);
        return () => clearTimeout(timer);
    }, [leadMode, dataLoading]);

    useEffect(() => {
        if (rerunMode === false) return;
        const loadExistingProperty = async() => {
            if (property && property.id) return;
            const user = await checkOnAuthStateChanged();
            if (user.status !== 200) {
                navigate("/login", { state: { path: '/verify-property' } });
                return;
            }
            setUserId(user.userId);
            let resolvedCompanyId = companyIdState;
            if (!resolvedCompanyId) {
                const userDoc = await getDocument("users", user.userId);
                if (userDoc.status === 200 && userDoc.data?.data?.companyId) {
                    resolvedCompanyId = userDoc.data.data.companyId;
                    setCompanyIdState(resolvedCompanyId);
                }
            }
            if (resolvedCompanyId && (routePropertyId || property?.id)) {
                const propId = property?.id || routePropertyId;
                const queryData = await getSubDocument("companies", resolvedCompanyId, "properties", String(propId));
                if (queryData.status === 200 && queryData.data?.data) {
                    const docData = queryData.data.data;
                    setPropertyState(docData);
                    if (docData?.propertyInfo) {
                        setBedrooms(docData.propertyInfo.bedrooms ?? 0);
                        setBathrooms(docData.propertyInfo.bathrooms ?? 0);
                        setSquareFootage(docData.propertyInfo.livingSquareFeet ?? 0);
                        setYearBuilt(docData.propertyInfo.yearBuilt ?? 0);
                        setBasement(docData.propertyInfo.basement ?? false);
                        if (docData.propertyInfo.basementSqFootage !== undefined) {
                            setBelowGradeSquareFootage(docData.propertyInfo.basementSqFootage ?? 0);
                        }
                        if (docData.propertyInfo.garageSpaces !== undefined) {
                            setGarageSpaces(docData.propertyInfo.garageSpaces ?? 1);
                        }
                        if (docData.propertyInfo.garageType !== undefined) {
                            setGarageType(docData.propertyInfo.garageType ?? null);
                        }
                    }
                }
            }
        };
        loadExistingProperty();
    }, [rerunMode, property, companyIdState, routePropertyId, navigate]);

    const companyId = companyIdState;
    const [bedrooms, setBedrooms] = useState(property === null ? 0 : property.propertyInfo.bedrooms === null ? 0 : property.propertyInfo.bedrooms);
    const [bathrooms, setBathrooms] = useState(property === null ? 0 : property.propertyInfo.bathrooms === null ? 0 : property.propertyInfo.bathrooms);
    const [squareFootage, setSquareFootage] = useState(property === null ? 0 : property.propertyInfo.livingSquareFeet === null ? 0 : property.propertyInfo.livingSquareFeet);
    const [belowGradeSquareFootage, setBelowGradeSquareFootage] = useState(property?.propertyInfo?.basementSqFootage ?? 0);
    const [yearBuilt, setYearBuilt] = useState(property === null ? 0 : property.propertyInfo.yearBuilt === null ? 0 : property.propertyInfo.yearBuilt);
    
    // Check if property is in Kansas City metro counties
    const isKansasCityCounty = property !== null && property.propertyInfo?.address?.fips ? (() => {
        const countyIdStr = typeof property.propertyInfo.address.fips === 'string' 
            ? property.propertyInfo.address.fips 
            : String(property.propertyInfo.address.fips);
        return kansasCityCountyFIPS.includes(countyIdStr);
    })() : false;
    const [sqFootageWarning, setSqFootageWarning] = useState(null);
    const [wrongMarket, setWrongMarket] = useState(false);
    // Property type is editable via Dropdown — REAPI sometimes returns "OTHER"
    // or an unfamiliar value, and the user may know better. We store the
    // canonical short-code key (SFR/MFR/CONDO/TWNH/MOBILE/LAND/OTHER) and only
    // resolve to the human label at render time. buildParams reads this state
    // so the value the lead is created with reflects the user's choice.
    const [propertyTypeKey, setPropertyTypeKey] = useState(() => normalizePropertyTypeKey(property?.propertyType));
    const [propertyTypeDropdownOpen, setPropertyTypeDropdownOpen] = useState(false);
    const [noMatchModal, setNoMatchModal] = useState(false);
    const [externalCompanyNoMatch, setExternalCompanyNoMatch] = useState(false);
    const [propertyInfoCompare, setPropertyInfoCompare] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [propertyNotes, setPropertyNotes] = useState(
        typeof initialPropertyNotes === 'string' ? initialPropertyNotes : '',
    );
    const [propertyImages, setPropertyImages] = useState(
        typeof initialImageUrl === 'string' ? initialImageUrl : ""
    );
    const reAPILotSize = property === null ? null : property.propertyInfo.lotSquareFeet;

    useEffect(() => {
        if ( state === null && !embeddedMode ) {
            navigate("/");
        }
    }, [navigate, state, embeddedMode]);

    // Re-seed the dropdown once the property finishes loading (legacy
    // non-embedded path), or when the parent swaps in a different property.
    // Skipped in embedded mode — initialProperty is already there and the
    // user may have already touched the dropdown, so we don't want to clobber
    // their selection.
    useEffect(() => {
        if (embeddedMode) return;
        if (property?.propertyType !== undefined) {
            setPropertyTypeKey(normalizePropertyTypeKey(property.propertyType));
        }
    }, [property?.propertyType, embeddedMode]);

    // Set default image source type based on forSale status
    useEffect(() => {
        if (imageSourceType === null && forSale !== null) {
            // Default to MLS images if property is for sale, otherwise share link
            setImageSourceType(forSale === true ? "mls" : "share_link");
        }
    }, [forSale, imageSourceType]);

    const matchIcon = (val, i) => {
        if ( val === "redfin" ) {
            return <Redfin key={`${val}-${i}`} className="verify-property-redfin-logo" />;
        }
        else if ( val === "zillow" ) {
            return <Zillow key={`${val}-${i}`} className="verify-property-provider-icon" />;
        }
        else if ( val === "realtor" ) {
            return <Realtor key={`${val}-${i}`} className="verify-property-provider-icon" />;
        }
        else if ( val === "realestateapi" ) {
            return <RealEstateAPI key={`${val}-${i}`} className="verify-property-provider-icon" />;
        }
        else {
            return null;
        }
    };

    // Provider icon order: Zillow → Redfin → Realtor → RealEstateAPI
    const renderProviderIcons = (providers = []) => {
        const list = (Array.isArray(providers) ? providers : [providers]).filter(Boolean);
        const priority = { zillow: 0, redfin: 1, realtor: 2, realestateapi: 3 };
        const ordered = [...new Set(list.map(p => String(p).toLowerCase()))].sort((a, b) => {
            const pa = priority.hasOwnProperty(a) ? priority[a] : 99;
            const pb = priority.hasOwnProperty(b) ? priority[b] : 99;
            return pa - pb;
        });
        return (
            <div className="verify-chip-provider-logos">
                {ordered.map((p, i) => (
                    matchIcon(p, i)
                ))}
            </div>
        );
    };

    const details = property === null ? [] : [
        {
            label: "Address",
            subLabel: null,
            value: property.propertyInfo.address.label,
            icon: null,
            input: true,
            editable: false,
            chips: []
        },
        {
            label: "Property type",
            subLabel: null,
            value: PROPERTY_TYPE_OPTIONS.find(o => o.key === propertyTypeKey)?.label || 'Other',
            icon: <PropertyIcon propertyType={propertyTypeKey} fill={colour.gray07} size={16} className="verify-property-icon" />,
            input: true,
            editable: true,
            inputKind: 'dropdown',
            chips: []
        },
        {
            label: "Beds",
            subLabel: null,
            value: `${bedrooms} bed`,
            rawValue: bedrooms,
            icon: <Bedroom fill={colour.gray07} size={16} className="verify-property-icon" />,
            input: true,
            editable: true,
            chips: dataOptions.find(e => e.type === "bedrooms") ? dataOptions.find(e => e.type === "bedrooms").values : []
        },
        {
            label: "Baths",
            subLabel: null,
            value: `${bathrooms} bath`,
            rawValue: bathrooms,
            icon: <Bathroom fill={colour.gray07} size={16} className="verify-property-icon" />,
            input: true,
            editable: true,
            chips: dataOptions.find(e => e.type === "bathrooms") ? dataOptions.find(e => e.type === "bathrooms").values : []
        },
        {
            label: "Living square footage",
            subLabel: null,
            value: `${numberFormatter.format(squareFootage)} sqft`,
            rawValue: squareFootage,
            icon: <SquareFootage fill={colour.gray07} className="verify-property-icon" />,
            input: true,
            editable: true,
            chips: dataOptions.find(e => e.type === "sqFootage") ? dataOptions.find(e => e.type === "sqFootage").values : []
        },
        {
            label: "Basement",
            subLabel: null,
            switchLabel: "Property has a basement",
            value: basement,
            rawValue: basement,
            icon: <HugeiconsIcon icon={EntranceStairsIcon} color={colour.gray07} size={16} className="verify-property-icon" />,
            input: false,
            editable: false,
            chips: []
        },
        {
            label: "Year built",
            subLabel: null,
            value: yearBuilt,
            rawValue: yearBuilt,
            icon: <YearBuilt fill={colour.gray07} className="verify-property-icon" />,
            input: true,
            editable: true,
            chips: []
        },
        {
            label: "Accessory Dwelling Unit (ADU)",
            subLabel: null,
            switchLabel: "Subject property has an ADU",
            value: adu,
            rawValue: adu,
            icon: <ADU fill={colour.gray07} className="verify-property-icon" />,
            input: false,
            editable: false,
            chips: []
        },
        {
            label: "Pool",
            subLabel: null,
            switchLabel: "Subject property has a pool",
            value: pool,
            rawValue: pool,
            icon: <HugeiconsIcon icon={PoolIcon} color={colour.gray07} size={16} className="verify-property-icon" />,
            input: false,
            editable: false,
            chips: []
        },
        {
            label: "Garage",
            subLabel: null,
            switchLabel: "Subject property has a garage",
            value: garage,
            rawValue: garage,
            icon: <HugeiconsIcon icon={CarParking01Icon} color={colour.gray07} size={16} className="verify-property-icon" />,
            input: false,
            editable: false,
            chips: []
        }
    ];

    const onChangeValue = (text, index) => {
        if ( index === 5 && text.length > 4 ) {
            return;
        }
        if ( index === 2 ) {
            setBedrooms(text);
            // Manually typed beds → deselect all bed chips
            setDataOptions((prev) => {
                if (!Array.isArray(prev)) return prev;
                return prev.map((group) => (
                    group?.type === "bedrooms" && Array.isArray(group.values)
                        ? { ...group, values: group.values.map((opt) => ({ ...opt, selected: false })) }
                        : group
                ));
            });
        }
        else if ( index === 3 ) {
            setBathrooms(text);
            // Manually typed baths → deselect all bath chips
            setDataOptions((prev) => {
                if (!Array.isArray(prev)) return prev;
                return prev.map((group) => (
                    group?.type === "bathrooms" && Array.isArray(group.values)
                        ? { ...group, values: group.values.map((opt) => ({ ...opt, selected: false })) }
                        : group
                ));
            });
        }
        else if ( index === 4 ) {
            setSquareFootage(text);
            // Manually typed sqft → deselect all sqft chips
            setDataOptions((prev) => {
                if (!Array.isArray(prev)) return prev;
                return prev.map((group) => (
                    group?.type === "sqFootage" && Array.isArray(group.values)
                        ? { ...group, values: group.values.map((opt) => ({ ...opt, selected: false })) }
                        : group
                ));
            });
            if ( Number(text) > 4000 ) {
                setSqFootageWarning(`FYI - this is NOT for lot size! Please double check ${numberFormatter.format(Number(text))} is only for the living area!`);
            }
            else {
                setSqFootageWarning(null);
            }
        }
        else if ( index === 6 ) {
            setYearBuilt(text);
        }
        else if ( index === 5 ) {
            // Below grade square footage (index 5, but only shown when basement is true)
            // Only allow setting if basement is true
            if (basement === true) {
                setBelowGradeSquareFootage(text);
            }
        }
    };

    const onChangePropertyNotes = (text) => {
        setPropertyNotes(text);
    };

    const onChangePropertyImages = (text) => {
        setPropertyImages(text);
    };

    const handleBasementChange = (value) => {
        setBasement(value);
        // Reset below grade square footage when basement is turned off
        if (value === false) {
            setBelowGradeSquareFootage(0);
        }
    };

    const handleGarageChange = (value) => {
        setGarage(value);
        // Reset garage fields when garage is turned off
        if (value === false) {
            setGarageSpaces(1);
            setGarageType(null);
        }
    };

    // Helper function to format garage label similar to PropertyCard.js
    const getGarageLabel = (hasGarage, garageSpacesVal, garageTypeVal) => {
        if (hasGarage !== true) {
            return "No Garage";
        }
        
        const hasSpaces = garageSpacesVal !== null && garageSpacesVal !== undefined && typeof garageSpacesVal === 'number' && garageSpacesVal > 0;
        const hasType = garageTypeVal === "attached" || garageTypeVal === "detached";
        
        // If we have both spaces and type info
        if (hasSpaces && hasType) {
            const typeAbbr = garageTypeVal === "attached" ? "att." : "det.";
            return `${garageSpacesVal} car garage (${typeAbbr})`;
        }
        
        // If we only have spaces info
        if (hasSpaces) {
            return `${garageSpacesVal} car garage`;
        }
        
        // If we only know there's a garage (no spaces or type info)
        return "Garage";
    };
    
    const openConditionImageModal = (index) => {
        setOpenScrollableImages(true);
        setSelectedConditionLevel(index);
    };

    const closeScrollableImages = () => {
        setOpenScrollableImages(false);
        setSelectedConditionLevel(null);
    };

    const selectChip = (chip, item) => {
        // Determine which field this chip belongs to
        let type = null;
        if (item?.label === "Beds") type = "bedrooms";
        else if (item?.label === "Baths") type = "bathrooms";
        else if (item?.label === "Living square footage") type = "sqFootage";

        if (!type) return;

        // Update the selected state within dataOptions (immutably)
        setDataOptions((prev) => {
            if (!Array.isArray(prev)) return prev;
            const updated = prev.map((group) => {
                if (group?.type !== type || !Array.isArray(group.values)) return group;
                const newValues = group.values.map((opt) => ({
                    ...opt,
                    selected: opt.rawValue === chip.rawValue
                }));
                return { ...group, values: newValues };
            });
            return updated;
        });

        // Sync the editable inputs with the chosen chip
        if (type === "bedrooms") {
            setBedrooms(chip.rawValue);
        } else if (type === "bathrooms") {
            setBathrooms(chip.rawValue);
        } else if (type === "sqFootage") {
            setSquareFootage(chip.rawValue);
            if (Number(chip.rawValue) > 4000) {
                setSqFootageWarning(`FYI - this is NOT for lot size! Please double check ${numberFormatter.format(Number(chip.rawValue))} is only for the living area!`);
            } else {
                setSqFootageWarning(null);
            }
        }
    };

    // Helper function to normalize null/undefined/0 values for comparison
    const normalizeValue = (val) => {
        if (val === null || val === undefined || val === 0 || val === "") {
            return null;
        }
        return val;
    };

    // Helper function to check if two values are equal, treating null/undefined/0 as equivalent
    const valuesMatch = (val1, val2) => {
        return normalizeValue(val1) === normalizeValue(val2);
    };

    const checkIfPropertySaved = async() => {
        const colRef = "companies";
        const docRef = companyId;
        const subColRef = "properties";
        const id = `${property.id}`;

        return new Promise(async(resolve) => {
            const queryData = await getSubDocument(colRef, docRef, subColRef, id);
            if ( queryData.status === 200 ) {
                const res = {
                    status: 200,
                    data: queryData.data.data
                }
                resolve(res);
            }
            else {
                const res = {
                    status: 400
                }
                resolve(res);
            }
        });
    };

    // Helper: Build params object for AWS query
    const buildParams = (prop, opts = {}) => {
        const params = {
            propertyId: String(prop.id),
            userId: userId,
            propertyAddress: prop.propertyInfo.address?.label,
            countyId: String(prop.propertyInfo.address?.fips || opts.fallbackCountyId),
            propertyType: propertyTypeKey || prop.propertyType,
            beds: prop.propertyInfo.bedrooms,
            baths: prop.propertyInfo.bathrooms,
            livingSquareFootage: prop.propertyInfo.livingSquareFeet,
            yearBuilt: prop.propertyInfo.yearBuilt,
            mortgageOwed: 0,
            additionalDebt: 0,
            miscSellerInfoDescription: "",
            buildingRedFlags: "",
            rehabMin: 0,
            rehabMax: 0,
            otherExpenses: 0,
            adu: adu,
            pool: pool,
            garage: garage,
            propertyNotes: propertyNotes,
            ...opts.extra
        };

        // Lot size handling
        if (typeof prop.propertyInfo.lotSquareFeet === "number" && prop.propertyInfo.lotSquareFeet > 15000) {
            const subjectLotSize = prop.propertyInfo.lotSquareFeet;
            const minLotSize = (subjectLotSize / 2) >= 43560 ? 43560 : (subjectLotSize / 2);
            params.scLotSqftMin = Math.round(minLotSize);
        }

        return params;
    };

    // Helper: Build default property data with basement/garage fields
    const buildDefaultPropertyData = async (prop, params) => {
        const data = await propertyDataReAPI.property({
            property: prop,
            userId: userId,
            propertyNotes: propertyNotes,
            adu: adu,
            pool: pool,
            garage: garage,
            basement: basement,
            belowGradeSquareFootage: basement === true ? Number(belowGradeSquareFootage) : 0,
            minLotSize: params.scLotSqftMin || 0,
            selectedConditionLevel: selectedConditionLevel
        });

        data.reAPILotSize = reAPILotSize;

        // Basement fields
        if (basement !== undefined && basement !== null) {
            data.basement = Boolean(basement);
        }
        if (basement === true && belowGradeSquareFootage !== undefined && belowGradeSquareFootage !== null) {
            const sqft = Number(belowGradeSquareFootage);
            if (!isNaN(sqft) && sqft >= 0) {
                data.basementSqFootage = sqft;
            }
        } else if (basement === false) {
            data.basementSqFootage = 0;
        }

        // Garage fields.
        //
        // 🚨 A `false` MUST CLEAR THE COUNT, NOT JUST OMIT IT. This block used to skip
        // garageSpaces/garageType entirely when garage was false, on the reasoning that not
        // writing a field is the same as not having one. It is not: every write from here goes
        // through `mergeSubData` (src/functions/firestore/index.js:97), which is
        // `setDoc(..., { merge: true })` — and a MERGE CANNOT REMOVE A KEY. The previous count
        // survived next to the new `garage: false`, so the doc said "no garage, 2 bays".
        // Measured 2026-09-08: 55 live properties in exactly that state.
        //
        // The pipeline already refuses to emit that shape — a positive space count PROMOTES the
        // boolean rather than being allowed to contradict it (inlineEnrichment.js:673,
        // checkSubjectAmenities/mainHandler.js:128), and arvClient.js:279-293 clears
        // spaces/type on an explicit "no garage". This is the same guard on the human path.
        //
        // 🪤 EXPLICIT `null`, NOT `deleteField()`, even though the sentinel is the house idiom
        // for wiping a key (RunCompsV2.js:687-718, useLeadMutation.js:63-65). This object has
        // THREE destinations, and only one of them is a Firestore merge: it is also handed to
        // `onLeadComplete` → Leads.js:2947, which builds a **createLeadManual cloud-function
        // payload**, and to the old-vs-new comparison UI below. A `deleteField()` sentinel is a
        // Firestore object; outside a write it is meaningless data. `null` is inert in all
        // three, reads identically to "absent" for every consumer (`??` and `Number()` both
        // treat it as nothing), and is exactly what the backend already writes for these two
        // keys on the same condition (inlineEnrichment.js:679-685).
        if (garage === true) {
            if (garageSpaces !== undefined && garageSpaces !== null) {
                const spaces = Number(garageSpaces);
                if (!isNaN(spaces) && spaces > 0) {
                    data.garageSpaces = spaces;
                }
            }
            if (garageType !== null && garageType !== undefined) {
                data.garageType = garageType;
            }
        } else if (garage === false) {
            data.garageSpaces = null;
            data.garageType = null;
        }

        // Extract financial details from full property object (MONTHLY fees)
        if (prop && typeof prop === 'object' && prop.financialDetails) {
            const financialDetails = prop.financialDetails;
            
            // Extract propertyTaxes (monthly)
            if (financialDetails.propertyTaxes !== null && financialDetails.propertyTaxes !== undefined) {
                const propertyTaxes = Number(financialDetails.propertyTaxes);
                if (!isNaN(propertyTaxes) && propertyTaxes >= 0) {
                    data.propertyTaxes = propertyTaxes;
                }
            }
            
            // Extract insurance (monthly)
            if (financialDetails.insurance !== null && financialDetails.insurance !== undefined) {
                const insurance = Number(financialDetails.insurance);
                if (!isNaN(insurance) && insurance >= 0) {
                    data.insurance = insurance;
                }
            }
            
            // Extract hoaFees (monthly)
            if (financialDetails.hoaFees !== null && financialDetails.hoaFees !== undefined) {
                const hoaFees = Number(financialDetails.hoaFees);
                if (!isNaN(hoaFees) && hoaFees >= 0) {
                    data.hoaFees = hoaFees;
                }
            }
        }

        return data;
    };

    // Helper: Build property info comparison object for no-match modal
    const buildPropertyInfoCompare = (oldData, newData, includeAmenities = true) => {
        const oldInfo = [
            { label: "Beds", value: `${oldData.beds} bed`, rawValue: oldData.beds },
            { label: "Baths", value: `${oldData.baths} bath`, rawValue: oldData.baths },
            { label: "Square footage", value: `${numberFormatter.format(oldData.sqft)} sqft`, rawValue: oldData.sqft },
            { label: "Year built", value: oldData.yearBuilt, rawValue: oldData.yearBuilt }
        ];

        const newInfo = [
            { label: "Beds", value: `${newData.beds} bed`, rawValue: newData.beds },
            { label: "Baths", value: `${newData.baths} bath`, rawValue: newData.baths },
            { label: "Square footage", value: `${numberFormatter.format(newData.sqft)} sqft`, rawValue: newData.sqft },
            { label: "Year built", value: newData.yearBuilt, rawValue: newData.yearBuilt }
        ];

        if (includeAmenities) {
            oldInfo.push(
                { label: "ADU", value: oldData.adu ? "ADU" : "No ADU", rawValue: oldData.adu ? true : false },
                { label: "Pool", value: oldData.pool ? "Pool" : "No Pool", rawValue: oldData.pool ? true : false },
                        {
                            label: "Garage",
                    value: getGarageLabel(oldData.garage, oldData.garageSpaces, oldData.garageType),
                            rawValue: {
                        garage: oldData.garage ? true : false,
                        garageSpaces: oldData.garageSpaces ?? null,
                        garageType: oldData.garageType ?? null
                    }
                },
                { label: "Basement", value: oldData.basement ? "Basement" : "No Basement", rawValue: oldData.basement ? true : false },
                        {
                            label: "Below grade square footage",
                    value: `${numberFormatter.format(oldData.basementSqFootage ?? 0)} sqft below grade`,
                    rawValue: oldData.basementSqFootage ?? 0
                }
            );

            newInfo.push(
                { label: "ADU", value: newData.adu ? "ADU" : "No ADU", rawValue: newData.adu ? true : false },
                { label: "Pool", value: newData.pool ? "Pool" : "No Pool", rawValue: newData.pool ? true : false },
                        {
                            label: "Garage",
                    value: getGarageLabel(newData.garage, newData.garageSpaces, newData.garageType),
                            rawValue: {
                        garage: newData.garage ? true : false,
                        garageSpaces: newData.garageSpaces ?? null,
                        garageType: newData.garageType ?? null
                    }
                },
                { label: "Basement", value: newData.basement ? "Basement" : "No Basement", rawValue: newData.basement ? true : false },
                        {
                            label: "Below grade square footage",
                    value: `${numberFormatter.format(newData.basementSqFootage ?? 0)} sqft below grade`,
                    rawValue: newData.basementSqFootage ?? 0
                }
            );
        }

        return { old: oldInfo, new: newInfo };
    };

    // Helper: Write property data to Firestore
    const writePropertyToFirestore = async (propId, data) => {
        try {
            await mergeSubData("companies", companyId, "properties", String(propId), data);
        } catch (error) {
            console.error("Failed to write property data:", error.message);
            throw error;
        }
    };

    // Helper: Normalize saved property data for comparison
    const normalizeSavedData = (savedData) => ({
        bedrooms: savedData.bedrooms,
        bathrooms: savedData.bathrooms,
        livingSquareFeet: savedData.livingSquareFeet,
        yearBuilt: savedData.yearBuilt,
        adu: savedData.adu,
        pool: savedData.pool,
        garage: savedData.garage,
        basement: savedData.basement === undefined ? false : savedData.basement,
        basementSqFootage: savedData.basementSqFootage === undefined ? 0 : savedData.basementSqFootage,
        garageSpaces: savedData.garage === false ? null : (savedData.garageSpaces === undefined ? null : savedData.garageSpaces),
        garageType: savedData.garage === false ? null : (savedData.garageType === undefined ? null : savedData.garageType)
    });

    // Helper: Check if saved data matches current params
    const savedDataMatchesParams = (saved, params) => {
        const currentGarageSpaces = garage === true ? Number(garageSpaces) : null;
        const currentGarageType = garage === true ? garageType : null;
        const currentBasementSqft = basement === true ? Number(belowGradeSquareFootage) : 0;

        return valuesMatch(saved.bedrooms, params.beds) &&
            valuesMatch(saved.bathrooms, params.baths) &&
            valuesMatch(saved.livingSquareFeet, params.livingSquareFootage) &&
            valuesMatch(saved.yearBuilt, params.yearBuilt) &&
            saved.adu === params.adu &&
            saved.pool === params.pool &&
            saved.garage === params.garage &&
            saved.basement === basement &&
            valuesMatch(saved.basementSqFootage ?? 0, currentBasementSqft) &&
            valuesMatch(saved.garageSpaces, currentGarageSpaces) &&
            saved.garageType === currentGarageType;
    };

    // Helper: Navigate to run-comps page
    const navigateToRunComps = (params, extraState = {}) => {
        navigate(`/run-comps/${params.countyId}/${params.propertyId}`, {
            state: {
                propertyEstimates: propertyEstimates,
                ...extraState
            }
        });
    };

    const checkValues = () => {
        if ( isNaN(Number(bedrooms)) || isNaN(Number(bathrooms)) || isNaN(Number(squareFootage)) || isNaN(Number(yearBuilt)) ) {
            setError("Please enter a valid number");
                return;
            }

        property.propertyInfo.bedrooms = Number(bedrooms);
        property.propertyInfo.bathrooms = Number(bathrooms);
        property.propertyInfo.livingSquareFeet = Number(squareFootage);
        property.propertyInfo.yearBuilt = Number(yearBuilt);
        setError(null);

        // Force the user to pick a real property type — REAPI sometimes
        // returns "OTHER" or an unfamiliar value and 'OTHER' is the catch-all
        // we normalise to. Anything else (including non-SFR types) is allowed
        // through; only "Other" blocks.
        if ( propertyTypeKey === 'OTHER' ) {
            setError("Please select a property type");
            return;
        }

        if ( property.propertyInfo.bedrooms === null || property.propertyInfo.bedrooms === 0 ) {
            setError("Please enter the number of bedrooms");
            return;
        }
        else if ( property.propertyInfo.bathrooms === null || property.propertyInfo.bathrooms === 0 ) {
            setError("Please enter the number of bathrooms");
            return;
        }
        else if ( property.propertyInfo.livingSquareFeet === null || property.propertyInfo.livingSquareFeet === 0 ) {
            setError("Please enter the square footage of the property");
            return;
        }
        else if ( property.propertyInfo.yearBuilt === null || property.propertyInfo.yearBuilt === 0 || property.propertyInfo.yearBuilt === "" ) {
            setError("Please enter the year the property was built");
            return;
        }

        showInsights();
    };

    const cta = async(reRun) => {
        // Validation
        if (!property || !property.id || !userId || !companyId) {
            setError("Missing required property information");
            return;
        }

        recordEvent("property_verified", {});
        setNoMatchModal(false);

        // Build params using helper
        const params = buildParams(property);
        const defaultPropertyData = await buildDefaultPropertyData(property, params);

        // Lead-creation flow: hand the confirmed/corrected fields back to the
        // caller and bail. Caller posts to createLeadManual which runs the
        // same enrichment path as email/SMS/MCP — no AWS lambda call, no
        // /run-comps navigation here. propertyImages carries any user-edited
        // Drive / Dropbox / direct image URL the caller can route into the
        // ingestion payload's imageFolders / imageUrls.
        if (leadMode === true && onLeadComplete) {
            setLoading(false);
            setError(null);
            onLeadComplete({ params, defaultPropertyData, propertyImages });
            return;
        }

        // Check if property was previously saved
        const checkSaved = await checkIfPropertySaved();
        
        if (checkSaved.status === 200) {
            const saved = normalizeSavedData(checkSaved.data);

            // Case 1: No bedrooms data saved or explicitly not a re-run
            if (checkSaved.data.bedrooms === undefined || reRun === false) {
                navigateToRunComps(params);
                return;
            }

            // Case 2: Values match and not forcing re-run
            if (reRun !== true && savedDataMatchesParams(saved, params)) {
                navigateToRunComps(params);
                return;
            }

            // Case 3: Values don't match and user hasn't decided yet - show modal
            if (reRun === null && !savedDataMatchesParams(saved, params)) {
                const currentGarageSpaces = garage === true ? Number(garageSpaces) : null;
                const currentGarageType = garage === true ? garageType : null;
                const currentBasementSqft = basement === true ? Number(belowGradeSquareFootage) : 0;

                setNoMatchModal(true);
                setLoading(false);
                setExternalCompanyNoMatch(false);
                setPropertyInfoCompare(buildPropertyInfoCompare(
                    {
                        beds: checkSaved.data.bedrooms,
                        baths: checkSaved.data.bathrooms,
                        sqft: checkSaved.data.livingSquareFeet,
                        yearBuilt: checkSaved.data.yearBuilt,
                        adu: checkSaved.data.adu,
                        pool: checkSaved.data.pool,
                        garage: checkSaved.data.garage,
                        garageSpaces: saved.garageSpaces,
                        garageType: saved.garageType,
                        basement: saved.basement,
                        basementSqFootage: saved.basementSqFootage
                    },
                    {
                        beds: params.beds,
                        baths: params.baths,
                        sqft: params.livingSquareFootage,
                        yearBuilt: params.yearBuilt,
                        adu: params.adu,
                        pool: params.pool,
                        garage: params.garage,
                        garageSpaces: currentGarageSpaces,
                        garageType: currentGarageType,
                        basement: basement,
                        basementSqFootage: currentBasementSqft
                    },
                    true // includeAmenities
                ));
                return;
            }
        }
        
        // Execute the property run
        setLoading(true);
        
        const getGatewayParams = {
            type: "lambdaDirect",
            lambdaParams: {
                ...params,
                forceRun: reRun === true
            }
        };

        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, 50);

        const url = process.env.REACT_APP_AWS_QUERY_URL;
        
        let queryData;
        try {
            queryData = await cloudFunctionV2(url, getGatewayParams);
        } catch (cloudFunctionError) {
            console.error("cloudFunctionV2 error:", cloudFunctionError.message);
            setError("Cloud function error: " + cloudFunctionError.message);
            setLoading(false);
            setExternalCompanyNoMatch(false);
            return;
        }
        
        // Handle non-200 responses
        if (queryData.status === 405) {
            setError("There was an error with the request, please contact us for more details");
            await sendErrorEmail({
                userId: userId,
                address: property.propertyInfo.address.label,
                notes: "Verify property page",
                propertyId: property.id
            });
            setLoading(false);
            setExternalCompanyNoMatch(false);
            return;
        }
        
        if (queryData.status !== 200) {
            if (queryData.body?.error) {
                setError(queryData.body.error);
            } else if (queryData.error && typeof queryData.error === "string") {
                setError(queryData.error);
            } else {
                setError("There was an error with the request, please try again");
            }
            setLoading(false);
            setExternalCompanyNoMatch(false);
            return;
        }

        // Handle 200 response
            const body = queryData.body;
        
        // Check for error in body
        if (body?.error !== undefined) {
                setLoading(false);
            if (body.error.includes("Invalid county")) {
                    setWrongMarket(true);
                    recordEvent("wrong_market", {});
            } else {
                    setWrongMarket(false);
                }
            setExternalCompanyNoMatch(false);
                return;
            }
        
                setWrongMarket(false);

        // Extract returned property data
        const returnedProperty = body.data?.propertyDetails ?? body.propertyDetails ?? null;
            const mlsPhotoUrls = returnedProperty?.listingDetails?.mlsPhotoUrls || [];
            
        // Check for external data mismatch
        if (returnedProperty !== null && reRun === null) {
            const attrs = returnedProperty.propertyAttributes;
            const hasAttributeMismatch = 
                attrs.bedrooms !== params.beds || 
                attrs.bathrooms !== params.baths || 
                attrs.livingSquareFootage !== params.livingSquareFootage || 
                attrs.yearBuilt !== params.yearBuilt;
            
            if (hasAttributeMismatch) {
                setNoMatchModal(true);
                setLoading(false);
                setExternalCompanyNoMatch(true);
                setPropertyInfoCompare(buildPropertyInfoCompare(
                    {
                        beds: attrs.bedrooms,
                        baths: attrs.bathrooms,
                        sqft: attrs.livingSquareFootage,
                        yearBuilt: attrs.yearBuilt
                    },
                    {
                        beds: params.beds,
                        baths: params.baths,
                        sqft: params.livingSquareFootage,
                        yearBuilt: params.yearBuilt
                    },
                    false // don't include amenities for external mismatch
                ));
                return;
            }
        }

        // Remove credit if this is a new run
        if (!(reRun === false && externalCompanyNoMatch === false)) {
            cloudFunctionV2(process.env.REACT_APP_REMOVE_CREDIT, { companyId: companyId }).catch(err => {
                    console.error("Error removing credit:", err);
                });
            }

        // Write to Firestore
        await writePropertyToFirestore(property.id, defaultPropertyData);
            
            // Calculate repair cost if images are available (fire and forget)
            const shouldCalculate = (imageSourceType === "mls" && mlsPhotoUrls.length > 0) || 
                                    (imageSourceType === "share_link" && propertyImages && propertyImages.trim() !== '');
            
            if (shouldCalculate) {
                calculateRepairCostFromImages(mlsPhotoUrls).catch(err => {
                    console.error('Background repair cost calculation failed:', err);
                });
            }
            
        // Save to recent searches
            defaultPropertyData.companyId = companyId;
            delete defaultPropertyData.loaded;
            await setDataRandomId("recent_searches", defaultPropertyData);

        navigateToRunComps(params);
            setError(null);
        setExternalCompanyNoMatch(false);
    };

    // Rerun-specific submit: overwrite property doc then trigger RunComps rerun flow
    const ctaRerun = async() => {
        // Validation
        if (!property || !property.id) {
            setError("Missing required property information");
            return;
        }
        if (!userId || !companyId) {
            setError("Missing required user or company information");
            return;
        }
        if (isNaN(Number(bedrooms)) || isNaN(Number(bathrooms)) || isNaN(Number(squareFootage)) || isNaN(Number(yearBuilt))) {
            setError("Please enter a valid number");
            return;
        }

        // Extract MLS images from property (available because property has already been run through AWS)
        const mlsPhotoUrls = property?.listingDetails?.mlsPhotoUrls || [];

        // Create updated property with form values
        const updatedProperty = { ...property };
        if (!updatedProperty.propertyInfo) updatedProperty.propertyInfo = {};
        updatedProperty.propertyInfo.bedrooms = Number(bedrooms);
        updatedProperty.propertyInfo.bathrooms = Number(bathrooms);
        updatedProperty.propertyInfo.livingSquareFeet = Number(squareFootage);
        updatedProperty.propertyInfo.yearBuilt = Number(yearBuilt);

        setPropertyState(updatedProperty);
        setError(null);
        setLoading(true);
        recordEvent("property_verify_rerun", {});

        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, 50);

        // Build params and default property data using helpers
        // Note: ctaRerun includes basement fields in params for onRerunComplete callback
        const params = buildParams(updatedProperty, { 
            fallbackCountyId: routeCountyId,
            extra: {
            basement: basement,
                basementSqFootage: basement === true ? Number(belowGradeSquareFootage) : 0
            }
        });
        const defaultPropertyData = await buildDefaultPropertyData(updatedProperty, params);

        // If onRerunComplete callback is provided, use it instead of navigating
        if (onRerunComplete) {
            setLoading(false);
            setError(null);
            onRerunComplete({ params, defaultPropertyData });
            return;
        }

        // Write to Firestore using helper
        await writePropertyToFirestore(updatedProperty.id, defaultPropertyData, 'Rerun');

        // Calculate repair cost if images are available (fire and forget)
        const shouldCalculate = (imageSourceType === "mls" && mlsPhotoUrls.length > 0) || 
                                (imageSourceType === "share_link" && propertyImages && propertyImages.trim() !== '');
        
        if (shouldCalculate) {
            calculateRepairCostFromImages(mlsPhotoUrls).catch(err => {
                console.error('Background repair cost calculation failed:', err);
            });
        }

        navigateToRunComps(params, { triggerRerun: true });
        setLoading(false);
        setError(null);
    };

    const calculateRepairCostFromImages = async (mlsPhotosFromAWS = null) => {
        // Check if we have images based on the source type
        if (imageSourceType === "share_link" && (!propertyImages || propertyImages.trim() === '')) {
            return; // Skip if manual mode but no URL provided
        }
        if (imageSourceType === "mls") {
            if (!mlsPhotosFromAWS || !Array.isArray(mlsPhotosFromAWS) || mlsPhotosFromAWS.length === 0) {
                return; // Skip if no MLS images provided
            }
        }

        try {
            const params = {
                verifiedParams: {
                    bedrooms: Number(bedrooms),
                    bathrooms: Number(bathrooms),
                    livingSquareFeet: Number(squareFootage),
                    yearBuilt: Number(yearBuilt)
                },
                companyId: companyId,
                propertyId: String(property.id),
                condition: amenityDescription || null,
                remodelLevel: selectedConditionLevel !== null && selectedConditionLevel !== undefined ? selectedConditionLevel : null
            };

            // Add image source based on type
            if (imageSourceType === "mls") {
                params.mlsImageUrls = mlsPhotosFromAWS;
                params.useMLSImages = true;
            } else {
                params.propertyImagesUrl = propertyImages;
                params.useMLSImages = false;
            }

            const response = await cloudFunctionV2(
                process.env.REACT_APP_CALCULATE_REPAIR_FROM_URL,
                params
            );
            
            // Log errors only
            if (response.body && response.body.skipped) {
                console.error('Repair cost calculation skipped:', response.body.error);
            } else if (response.body && response.body.error) {
                console.error('Repair cost calculation error:', response.body.error);
            }
        } catch (error) {
            console.error('Error calculating repair cost:', error);
        }
    };

    const showInsights = () => {
        setInsights(true);
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
        }, 100);
    };

    const backInsights = () => {
        setInsights(false);
    };

    const backHome = () => {
        recordEvent("back_to_home", {});
        navigate("/");
    };

    const closeNoMatchModal = () => {
        setNoMatchModal(false);
        setExternalCompanyNoMatch(false);
        setSelectedVersion(null);
    };

    const selectConditionLevel = (index) => {
        setSelectedConditionLevel(index);
    };

    const clickIndividualImage = (index) => {
        setSelectedImageIndex(index);
        setOpenImageGallery(true);
    };

    const closeImageGallery = () => {
        setOpenImageGallery(false);
    };

    const closePropertyImageGallery = () => {
        setOpenPropertyImageGallery(false);
    };

    const openPropertyImageGalleryFunc = (index) => {
        setSelectedPropertyImageIndex(index);
        setOpenPropertyImageGallery(true);
    };

    // Sync dark background with modal visibility
    useEffect(() => {
        setShowDarkBackground(noMatchModal);
        return () => {
            setShowDarkBackground(false);
        };
    }, [noMatchModal, setShowDarkBackground]);

    return (
        <div className={"verify-property-outer-container " + (loading === true ? "bg-colour-black " : "")}>
            {
                openPropertyImageGallery === true &&
                <ImageGallery
                    alt={property.propertyInfo.address.label}
                    images={images}
                    imageIndex={selectedPropertyImageIndex}
                    setImageIndex={setSelectedPropertyImageIndex}
                    closeImageGallery={closePropertyImageGallery}
                />
            }
            {
                openImageGallery === true &&
                selectedConditionLevel !== null &&
                conditionLevels &&
                conditionLevels[selectedConditionLevel] &&
                <ImageGallery
                    alt={conditionLevels[selectedConditionLevel].label}
                    images={conditionLevels[selectedConditionLevel].images}
                    imageIndex={selectedImageIndex}
                    setImageIndex={setSelectedImageIndex}
                    closeImageGallery={closeImageGallery}
                />
            }
            {
                openScrollableImages === true &&
                selectedConditionLevel !== null &&
                conditionLevels &&
                conditionLevels[selectedConditionLevel] &&
                Array.isArray(conditionLevels[selectedConditionLevel].images) &&
                <ScrollableImages
                    images={conditionLevels[selectedConditionLevel].images}
                    closeScrollableImages={closeScrollableImages}
                    imageClick={clickIndividualImage}
                    condition={null}
                />
            }
            {
                loading === true ?
                <div className="verify-property-inner-container relative-container">
                    <ReportLoadingAnimation />
                </div>
                :
                wrongMarket === true ?
                <div className="verify-property-inner-container relative-container">
                    <div className="verify-property-title-container">
                        <h1 className="heading-large-bold colour-primary text-align-center">
                            We're not active in this market yet
                        </h1>
                        <p className="body-regular colour-primary text-align-center">
                            We only have access to Tampa Bay, FL, St. Louis, MO, Kansas City, MO/KS and Maricopa County, AZ right now. 
                            If you need more counties,
                            send us an email at <a href="mailto:info@clozers.co" className="body-bold colour-primary">info@clozers.co</a> and we'll see what we can do!
                        </p>
                    </div>
                    <div className="verify-property-wrong-market-container">
                        <Button
                            buttonType="primary-button"
                            size="large"
                            text={"Back to home"}
                            cta={() => backHome()}
                            disabled={false}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                    </div>
                </div>
                :
                dataLoading === true ?
                <div className="verify-property-data-pull-animation-container">
                    {
                        // leadMode hides the form (and its Back button) behind the
                        // loader, so surface an escape hatch here — otherwise a
                        // slow/stuck pull would trap the user in the full-screen
                        // overlay (no nav chrome) until the 25s safety timeout.
                        leadMode === true && onLeadBack &&
                        <div
                            className="insights-text-button text-button button-row gap-x-small"
                            onClick={() => onLeadBack()}
                        >
                            <Arrow className="" fill={colour.gray06} />
                            <span className="body-regular colour-primary">
                                Back
                            </span>
                        </div>
                    }
                    <DataPullAnimation />
                </div>
                :
                <div className="verify-property-inner-container relative-container">
                    {
                        loading === true &&
                        <Loading />
                    }
                    {
                        noMatchModal === true &&
                        <Modal
                            heading="Conflicting property info"
                            subheading={null}
                            body={<NoMatchModal propertyInfoCompare={propertyInfoCompare} externalCompanyNoMatch={externalCompanyNoMatch} selectedOption={selectedVersion} setSelectedOption={setSelectedVersion} setBedrooms={setBedrooms} setBathrooms={setBathrooms} setSquareFootage={setSquareFootage} setYearBuilt={setYearBuilt} setAdu={setAdu} setPool={setPool} setGarage={setGarage} setGarageSpaces={setGarageSpaces} setGarageType={setGarageType} setBasement={setBasement} setBelowGradeSquareFootage={setBelowGradeSquareFootage} />}
                            type="basic"
                            cancelText={"Close"}
                            cancelButtonCTA={closeNoMatchModal}
                            actionButtonText={"Confirm"}
                            actionButtonCTA={() => cta(selectedVersion === 0 ? false : selectedVersion === 1 ? true : null)}
                            secondaryButtonText={null}
                            secondaryButtonCTA={null}
                            exitButtonCTA={closeNoMatchModal}
                            loading={false}
                            actionButtonDisabled={selectedVersion === null ? true : false}
                            leftIcon={null}
                            rightIcon={null}
                            cancelLeftIcon={null}
                            cancelRightIcon={null}
                            customClass="verify-property-no-match-modal"
                        />
                    }
                    {
                        (insights === true || embeddedMode) &&
                        <div
                            className="insights-text-button text-button button-row gap-x-small"
                            onClick={() => {
                                if (insights === false && rerunMode === true && onRerunBack) {
                                    onRerunBack();
                                } else if (insights === false && leadMode === true && onLeadBack) {
                                    onLeadBack();
                                } else {
                                    backInsights();
                                }
                            }}
                        >
                            <Arrow
                               className=""
                               fill={colour.gray06} 
                            />
                            <span className="body-regular colour-primary">
                                Back
                            </span>
                        </div>
                    }
                    <div className="verify-property-title-container">
                        <h1 className="heading-large-bold colour-primary text-align-center">
                            {
                                insights === true
                                    ? (leadMode ? "Almost done" : "Insights")
                                    : (rerunMode ? "Re-run report" : (leadMode ? "Confirm the details" : "Verify basic info"))
                            }
                        </h1>
                        <span className="body-regular colour-secondary block-text text-align-center">
                            {
                                insights === true
                                    ? (leadMode ? "Add notes or finish level if you have them." : "Tell us a bit more to help our analysis.")
                                    : (leadMode ? "Confirm or correct the property facts before we underwrite." : "Please verify that our data for this property is correct.")
                            }
                        </span>
                        {
                            testMode === true && property !== null &&
                            <span className="body-regular colour-primary">
                                {property.id}
                            </span>
                        }
                        {
                            testMode === true && property !== null &&
                            <span className="body-regular colour-primary">
                                {property.propertyInfo.address.county}
                            </span>
                        }
                    </div>
                    <div className="verify-property-details-container">
                        {
                            insights === false &&
                            details.map((item, index) =>
                                item.input === true &&
                                <React.Fragment key={index}>
                                    <div className="verify-property-details-element">
                                        <div className="verify-property-details-element-left-container">
                                            <div className="verify-property-details-label-outer-container">
                                                <div className="verify-property-details-label-container">
                                                    {item.icon}
                                                    <span className="body-bold colour-primary">
                                                        {item.label}
                                                    </span>
                                                    {
                                                        index === 4 && isKansasCityCounty === true &&
                                                        <span className="body-regular colour-secondary">
                                                            (Above grade)
                                                        </span>
                                                    }
                                                </div>
                                            </div>
                                            {
                                                item.editable === false ?
                                                <span className="body-regular colour-primary">
                                                    {item.value}
                                                </span>
                                                :
                                                item.inputKind === 'dropdown' ?
                                                <div className="verify-property-details-dropdown-container">
                                                    <Dropdown
                                                        open={propertyTypeDropdownOpen}
                                                        icon={null}
                                                        label={item.value}
                                                        disabled={false}
                                                        options={PROPERTY_TYPE_OPTIONS.map(opt => ({ label: opt.label }))}
                                                        multiselect={false}
                                                        cta={(_optItem, optIndex) => {
                                                            const nextKey = PROPERTY_TYPE_OPTIONS[optIndex].key;
                                                            setPropertyTypeKey(nextKey);
                                                            setPropertyTypeDropdownOpen(false);
                                                            // Clear the OTHER-block error the moment they pick a real
                                                            // type — otherwise the stale "Please select a property
                                                            // type" message lingers until the next Next-click.
                                                            if (nextKey !== 'OTHER' && error === 'Please select a property type') {
                                                                setError(null);
                                                            }
                                                        }}
                                                        sidealign="left"
                                                        topalign="bottom"
                                                        toggleDropdown={() => setPropertyTypeDropdownOpen(o => !o)}
                                                        indexSelect={true}
                                                        selectedIndex={PROPERTY_TYPE_OPTIONS.findIndex(o => o.key === propertyTypeKey)}
                                                    />
                                                </div>
                                                :
                                                <div className="verify-property-details-edit-container">
                                                    <Input
                                                        label={null}
                                                        labelType={null}
                                                        info={false}
                                                        value={item.rawValue}
                                                        onChange={(text) => onChangeValue(text.target.value, index)}
                                                        placeholder={item.rawValue}
                                                        type="text"
                                                        leftUnit={null}
                                                        rightUnit={null}
                                                        unitSwitch={false}
                                                        leftUnitSelected={true}
                                                        unitSwitchFunc={null}
                                                        disabled={false}
                                                        multiline={false}
                                                        error={item.rawValue === 0 || item.rawValue === "" ? true : false}
                                                        onMouseEnter={null}
                                                        onMouseLeave={null}
                                                        reset={false}
                                                        resetFunc={null}
                                                        resetPosition={null}
                                                        focusMultiline={false}
                                                        prompt={false}
                                                    />
                                                </div>
                                            }
                                            {
                                                index === 0 &&
                                                <div className="property-links-container">
                                                    {(() => {
                                                        const links = [
                                                            { label: "Zillow", href: zillowURL },
                                                            { label: "Realtor", href: realtorURL },
                                                            { label: "Redfin", href: redfinURL }
                                                        ].filter(l => l.href !== null);

                                                        if (links.length === 0) return null;

                                                        return links.map((l, i) => (
                                                            <React.Fragment key={l.label}>
                                                                <a 
                                                                    className="label-regular cursor-pointer colour-secondary"
                                                                    href={l.href}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {l.label}
                                                                </a>
                                                                {i < links.length - 1 && (
                                                                    <span className="label-regular colour-secondary">•</span>
                                                                )}
                                                            </React.Fragment>
                                                        ));
                                                    })()}
                                                </div>
                                            }
                                            {
                                                index === 4 && sqFootageWarning !== null &&
                                                <span className="body-regular colour-error">
                                                    {sqFootageWarning}
                                                </span>
                                            }
                                        </div>
                                        {
                                            item.chips.length > 0 &&
                                            <div className="verify-property-details-chips">
                                                {
                                                    item.chips.map((chip, ii) => 
                                                        chip.value === 0 || chip.value === "" || chip.value === null ? null :
                                                        <SelectionChip
                                                            label={chip.value}
                                                            selected={chip.selected}
                                                            disabled={false}
                                                            func={() => selectChip(chip, item)}
                                                            index={ii}
                                                            leftIcon={renderProviderIcons(chip.providers && chip.providers.length > 0 ? chip.providers : [chip.provider])}
                                                            rightIcon={null}
                                                            darkMode={false}
                                                            key={ii}
                                                        />
                                                    )
                                                }
                                            </div>
                                        }
                                    </div>
                                    <div className="light-grey-divider">
                                    </div>
                                    {
                                        index === 4 &&
                                        <React.Fragment>
                                            <div className="verify-property-details-switch-element">
                                                <div className="verify-property-details-switch-title">
                                                    {details[5].icon}
                                                    <span className="body-bold colour-primary">
                                                        {details[5].label}
                                                    </span>
                                                </div>
                                                <Switch
                                                    label={details[5].switchLabel}
                                                    val={basement}
                                                    setVal={handleBasementChange}
                                                />
                                            </div>
                                            {
                                                basement === false &&
                                                <div className="light-grey-divider">
                                                </div>
                                            }
                                            {
                                                basement === true &&
                                                <React.Fragment>
                                                    <div className="verify-property-details-element">
                                                        <div className="verify-property-details-element-left-container">
                                                            <div className="verify-property-details-label-outer-container">
                                                                <div className="verify-property-details-label-container">
                                                                    <SquareFootage fill={colour.gray07} className="verify-property-icon" />
                                                                    <span className="body-bold colour-primary">
                                                                        Below grade square footage
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="verify-property-details-edit-container">
                                                                <Input
                                                                    label={null}
                                                                    labelType={null}
                                                                    info={false}
                                                                    value={belowGradeSquareFootage}
                                                                    onChange={(text) => onChangeValue(text.target.value, 5)}
                                                                    placeholder={belowGradeSquareFootage}
                                                                    type="text"
                                                                    leftUnit={null}
                                                                    rightUnit={null}
                                                                    unitSwitch={false}
                                                                    leftUnitSelected={true}
                                                                    unitSwitchFunc={null}
                                                                    disabled={false}
                                                                    multiline={false}
                                                                    error={false}
                                                                    onMouseEnter={null}
                                                                    onMouseLeave={null}
                                                                    reset={false}
                                                                    resetFunc={null}
                                                                    resetPosition={null}
                                                                    focusMultiline={false}
                                                                    prompt={false}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="light-grey-divider">
                                                    </div>
                                                </React.Fragment>
                                            }
                                        </React.Fragment>
                                    }
                                </React.Fragment>
                            )
                        }
                        {
                            insights === false &&
                            <div className="verify-property-details-amenities-container">
                                <div className="verify-property-details-amenities-title-container">
                                    <span className="body-bold colour-primary">
                                        Additional features
                                    </span>
                                    {
                                        amenityDescription !== null &&
                                        <span className="body-regular colour-primary">
                                            {amenityDescription}
                                        </span>
                                    }
                                </div>
                                {
                                    images.length > 0 &&
                                    <div className="verify-property-details-amenities-images">
                                        {
                                            images.map((image, index) => 
                                                <div
                                                    className="verify-property-details-amenities-image-container"
                                                    key={index}
                                                >
                                                    <img
                                                        src={image}
                                                        className="verify-property-details-amenities-image cursor-pointer"
                                                        alt=""
                                                        onClick={() => openPropertyImageGalleryFunc(index)}
                                                    />
                                                </div>
                                            )
                                        }
                                    </div>
                                }
                                {
                                    details.map((item, index) => 
                                        item.input === false && index !== 5 &&
                                        <React.Fragment key={index}>
                                            <div className="verify-property-details-switch-element">
                                                <div className="verify-property-details-switch-title">
                                                    {item.icon}
                                                    <span className="body-bold colour-primary">
                                                        {item.label}
                                                    </span>
                                                </div>
                                                <Switch
                                                    label={item.switchLabel}
                                                    val={index === 7 ? adu : index === 8 ? pool : index === 9 ? garage : null}
                                                    setVal={index === 7 ? setAdu : index === 8 ? setPool : index === 9 ? handleGarageChange : null}
                                                />
                                            </div>
                                            {
                                                index === 9 && garage === false &&
                                                <div className="light-grey-divider">
                                                </div>
                                            }
                                            {
                                                index === 9 && garage === true &&
                                                <React.Fragment>
                                                    <div className="verify-property-garage-fields-row">
                                                        <div className="verify-property-details-element">
                                                            <div className="verify-property-details-element-left-container">
                                                                <div className="verify-property-details-label-outer-container">
                                                                    <div className="verify-property-details-label-container">
                                                                        <SquareFootage fill={colour.gray07} className="verify-property-icon" />
                                                                        <span className="body-bold colour-primary">
                                                                            Garage spaces
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="verify-property-details-edit-container">
                                                                    <Input
                                                                        label={null}
                                                                        labelType={null}
                                                                        info={false}
                                                                        value={garageSpaces}
                                                                        onChange={(text) => setGarageSpaces(text.target.value)}
                                                                        placeholder={garageSpaces}
                                                                        type="text"
                                                                        leftUnit={null}
                                                                        rightUnit={null}
                                                                        unitSwitch={false}
                                                                        leftUnitSelected={true}
                                                                        unitSwitchFunc={null}
                                                                        disabled={false}
                                                                        multiline={false}
                                                                        error={false}
                                                                        onMouseEnter={null}
                                                                        onMouseLeave={null}
                                                                        reset={false}
                                                                        resetFunc={null}
                                                                        resetPosition={null}
                                                                        focusMultiline={false}
                                                                        prompt={false}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="verify-property-details-element">
                                                            <div className="verify-property-details-element-left-container">
                                                                <div className="verify-property-details-label-outer-container">
                                                                    <div className="verify-property-details-label-container">
                                                                        <HugeiconsIcon icon={CarParking01Icon} color={colour.gray07} size={16} className="verify-property-icon" />
                                                                        <span className="body-bold colour-primary">
                                                                            Garage type
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="verify-property-details-radio-container">
                                                                    <Radio
                                                                        label="Attached"
                                                                        type="regular"
                                                                        selected={garageType === "attached"}
                                                                        identifier="attached"
                                                                        selectRadio={(id) => setGarageType(id)}
                                                                    />
                                                                    <Radio
                                                                        label="Detached"
                                                                        type="regular"
                                                                        selected={garageType === "detached"}
                                                                        identifier="detached"
                                                                        selectRadio={(id) => setGarageType(id)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="light-grey-divider">
                                                    </div>
                                                </React.Fragment>
                                            }
                                        </React.Fragment>
                                    )
                                }
                            </div>
                        }
                        {
                            insights === true &&
                            <div className="verify-property-details-element">
                                <Input
                                    label={"Unique insights?"}
                                    labelType={"default"}
                                    info={false}
                                    value={propertyNotes}
                                    onChange={(text) => onChangePropertyNotes(text.target.value)}
                                    placeholder={"This area/property is difficult to comp. You have to watch out for XYZ..."}
                                    type="text"
                                    leftUnit={null}
                                    rightUnit={null}
                                    unitSwitch={false}
                                    leftUnitSelected={true}
                                    unitSwitchFunc={null}
                                    disabled={false}
                                    multiline={true}
                                    error={false}
                                    onMouseEnter={null}
                                    onMouseLeave={null}
                                    reset={false}
                                    resetFunc={null}
                                    resetPosition={null}
                                    focusMultiline={false}
                                    prompt={false}
                                />
                            </div>
                        }
                        {
                            insights === true &&
                            <div className="light-grey-divider margin-tops">
                            </div>
                        }
                        {
                            insights === true && showImageInput === true &&
                            <div className="verify-property-finish-level-element">
                                <div className="verify-property-finish-level-title">
                                    <span className="body-bold colour-primary">
                                        Property images
                                    </span>
                                    <span className="body-regular colour-secondary">
                                        {
                                            imageSourceType === "mls" 
                                            ? "Use MLS listing images for repair cost analysis."
                                            : "Paste a link to the property images to get a repair estimate. "
                                        }
                                        {
                                            imageSourceType === "share_link" &&
                                            <span className="text-italic">Must be Google drive, Dropbox, or Onedrive</span>
                                        }
                                    </span>
                                </div>
                                <div className="verify-property-details-element">
                                    <div className="verify-property-details-element-left-container">
                                        <div className="verify-property-details-radio-container">
                                            <Radio
                                                label="MLS listing images"
                                                type="regular"
                                                selected={imageSourceType === "mls"}
                                                identifier="mls"
                                                selectRadio={(id) => setImageSourceType(id)}
                                            />
                                            <Radio
                                                label="Share link"
                                                type="regular"
                                                selected={imageSourceType === "share_link"}
                                                identifier="share_link"
                                                selectRadio={(id) => setImageSourceType(id)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {
                                    imageSourceType === "share_link" &&
                                    <Input
                                        label={null}
                                        labelType={null}
                                        info={false}
                                        value={propertyImages}
                                        onChange={(text) => onChangePropertyImages(text.target.value)}
                                        placeholder={"https://drive.google.com/4389fhr2"}
                                        type="text"
                                        leftUnit={null}
                                        rightUnit={null}
                                        unitSwitch={false}
                                        leftUnitSelected={true}
                                        unitSwitchFunc={null}
                                        disabled={false}
                                        multiline={false}
                                        error={false}
                                        onMouseEnter={null}
                                        onMouseLeave={null}
                                        reset={false}
                                        resetFunc={null}
                                        resetPosition={null}
                                        focusMultiline={false}
                                        prompt={false}
                                    />
                                }
                            </div>
                        }
                        {
                            insights === true && showImageInput === true &&
                            <div className="light-grey-divider margin-tops">
                            </div>
                        }
                        {
                            insights === true &&
                            <div className="verify-property-finish-level-element">
                                <div className="verify-property-finish-level-title">
                                    <span className="body-bold colour-primary">
                                        Finish level
                                    </span>
                                    <span className="body-regular colour-secondary">
                                        What type of remodel are you going to do?
                                    </span>
                                </div>
                                {
                                    conditionLevels.map((item, index) =>
                                        <FinishLevel
                                            title={item.label}
                                            description={item.description}
                                            image={item.featuredImage}
                                            openConditionImageModal={openConditionImageModal}
                                            index={index}
                                            key={index}
                                            selected={selectedConditionLevel === index ? true : false}
                                            selectConditionLevel={selectConditionLevel}
                                        />   
                                    )
                                }
                            </div>
                        }
                    </div>
                    <div className="verify-property-button-container">
                        <Button
                            buttonType="primary-button"
                            size="large"
                            text={insights === true ? (rerunMode ? "Save & re-run for 1 credit" : (leadMode ? "Create lead" : "Finish")) : "Next"}
                            cta={() => insights === true ? (rerunMode ? ctaRerun() : cta(null)) : checkValues()}
                            disabled={insights === true && !leadMode && selectedConditionLevel === null ? true : (loading || submitting)}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={submitting === true}
                        />
                    </div>
                    {
                        error !== null &&
                        <div className="error-message-container">
                            <span className="body-regular colour-error">
                                {error}
                            </span>
                        </div>
                    }
                </div>
            }
        </div>
    )
};

export default VerifyProperty;